<template>
  <svg
    class="badges-layer"
    :viewBox="`0 0 ${design.w} ${design.h}`"
    preserveAspectRatio="none"
    width="100%"
    height="100%"
    aria-hidden="true"
    focusable="false"
  >
    <g v-for="(b, i) in badges" :key="i">
      <rect
        :x="b.x0"
        :y="b.y0"
        :width="b.w"
        :height="b.h"
        :rx="radius"
        :ry="radius"
        :fill="fill"
        :stroke="stroke"
        :stroke-width="1"
      />
      <text
        :x="b.cx"
        :y="b.cy"
        :font-size="fontPx"
        text-anchor="middle"
        dominant-baseline="middle"
        lengthAdjust="spacingAndGlyphs"
        :textLength="Math.max(0, b.w - padX * 2)"
        fill="#000000"
      >
        {{ b.label }}
      </text>
    </g>
  </svg>
</template>

<script>
export default {
  name: "BoardBadgesSVG",
  props: {
    nodes: { type: Array, default: () => [] },     // full nodes with .elements
    design: { type: Object, required: true },      // { w, h }
    pointsPerStep: { type: Number, default: 100 }, // backend
    every: { type: Number, default: 800 },         // badge every N points
    lookAhead: { type: Number, default: 2 },       // skip ahead if crowded node
    offsetPx: { type: Number, default: 20 },       // perpendicular offset
    fontPx: { type: Number, default: 13 },         // design-space font
    minCardW: { type: Number, default: 34 },       // min card width
    padX: { type: Number, default: 10 },
    padY: { type: Number, default: 10 },
    // theming
    fill: { type: String, default: "#ffffff" },
    stroke: { type: String, default: "#00000033" },
    radius: { type: Number, default: 6 },
    // simple self-avoid (distance between badge centers)
    minGap: { type: Number, default: 40 }
  },
  computed: {
    badges() {
      const nodes = this.nodes || []
      const ptsPer = this.pointsPerStep || 100
      if (!nodes.length || ptsPer <= 0) return []

      // total points at END
      const endIdx = this._endIndex(nodes)
      const lastPts = endIdx * ptsPer
      const out = []

      for (let pts = this.every; pts <= lastPts; pts += this.every) {
        // 1) pick a node index and look ahead for a free spot
        let idx = Math.round(pts / ptsPer)
        let hops = 0
        while (idx < nodes.length && this._hasElements(nodes[idx]) && hops < this.lookAhead) {
          idx++; hops++
        }
        if (idx >= nodes.length || this._hasElements(nodes[idx])) continue

        // 2) base + previous node (for direction)
        const n = nodes[idx]
        const p = nodes[Math.max(0, idx - 1)] || n
        const dx = n.x - p.x, dy = n.y - p.y
        const len = Math.sqrt(dx*dx + dy*dy) || 1
        // perpendicular normal (unit)
        const nx = -dy / len, ny = dx / len
        const side = (idx % 2 === 0 ? 1 : -1) // alternate sides
        const cx = n.x + nx * this.offsetPx * side
        const cy = n.y + ny * this.offsetPx * side

        // 3) card size
        const label = pts + "\u202Fp"
        const w = Math.max(this.minCardW, this.fontPx * 4 + this.padX * 2) // ~room for "XXXX p"
        const h = Math.round(this.fontPx + this.padY * 2)
        const x0 = Math.round(cx - w / 2)
        const y0 = Math.round(cy - h / 2)

        // 4) self-avoid: skip if too close to previous badge centers
        if (out.some(prev => Math.hypot(prev.cx - cx, prev.cy - cy) < this.minGap)) continue

        out.push({ x0, y0, w, h, cx, cy, label })
      }
      return out
    }
  },
  methods: {
    _hasElements(node) {
      const els = (node && node.elements) || []
      return els && els.length > 0
    },
    _endIndex(nodes) {
      const end = nodes.find(n => n && n.type === 'END')
      return end ? end.index : Math.max(0, nodes.length - 1)
    }
  }
}
</script>

<style scoped>
.badges-layer{
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 3; /* above path (z:2), below icons/avatars (z:3+/4) */
}
</style>
